/**
 * Device pairing (DEVICE-PAIRING, D561, ADR-0116): the lifecycle of a registered device - a wall TV of the operations room, a
 * kiosk in a lobby - that is not a person and signs in with no e-mail, password or second factor.
 *
 *   registered --(a single-use pairing code is redeemed on the device)--> paired --(an operator revokes it)--> revoked
 *   registered --(an operator revokes it)--> revoked
 *
 * A REGISTRATION is one row: it is paired at most ONCE, so at most one device ever holds its token (a replacement is a new
 * registration after the old one is revoked - REVOKED is terminal). The token is permanent until revoked, carries a fixed,
 * kind-derived set of scopes and belongs to exactly one market; the server only ever stores its hash. Every transition carries
 * a stable reason code and the policy version that decided it (AGENTS.md).
 *
 * Pure: no clock, no randomness, no I/O. The secrets live in `secrets.ts`; persistence is `@vello/device-pairing-store`.
 * Neutral: no country, currency, language or city is named here; a market is the opaque `cc:slug` id.
 */
import { err, ok } from '@vello/core';
/** What a registered device is for. A new kind needs its scopes below and a migration (the table's CHECK). */
export const DEVICE_KINDS = ['ops_tv', 'kiosk'];
export const DEVICE_STATES = ['registered', 'paired', 'revoked'];
/** What a paired device may do. The set is derived from the KIND, never chosen per device, so a token cannot be over-scoped. */
export const DEVICE_SCOPES = ['live_ops:read', 'kiosk:order'];
export const DEVICE_SCOPES_BY_KIND = {
    ops_tv: ['live_ops:read'],
    kiosk: ['kiosk:order'],
};
/** Why an operator revokes a device (a closed list: the audit trail is read by people, not free text). */
export const DEVICE_REVOKE_REASONS = ['lost_or_stolen', 'replaced', 'decommissioned', 'suspected_compromise', 'other'];
/** The append-only audit trail of a device. */
export const DEVICE_EVENT_TYPES = ['registered', 'code_issued', 'paired', 'pairing_refused', 'revoked', 'deleted'];
/** Every stable refusal reason of the pairing surface (operator routes, the public redemption and the device's own calls). */
export const DEVICE_PAIRING_REFUSALS = [
    'DEVICE_KIND_INVALID',
    'DEVICE_LABEL_INVALID',
    'DEVICE_LABEL_TAKEN',
    'DEVICE_NOT_FOUND',
    'DEVICE_REVOKED',
    'DEVICE_ALREADY_PAIRED',
    'DEVICE_REVOKE_REASON_INVALID',
    'DEVICE_NOT_REVOKED',
    'PAIRING_CODE_INVALID',
    'PAIRING_CODE_EXPIRED',
    'PAIRING_CODE_USED',
    'PAIRING_CODE_SUPERSEDED',
    'PAIRING_RATE_LIMITED',
    'DEVICE_TOKEN_INVALID',
    'DEVICE_SCOPE_DENIED',
];
/** The version of the rules below; it rides on every event so a decision can be reproduced. */
export const DEVICE_PAIRING_POLICY_VERSION = 'device-pairing/1';
export const DEVICE_PAIRING_POLICY = {
    /** How long an issued pairing code can be redeemed. */
    codeTtlSeconds: 900,
    /** The number of symbols of a pairing code (shown in two groups). */
    codeLength: 8,
    labelMaxLength: 80,
    /** A device's `last_seen_at` moves at most once per this many seconds (an authenticated poll is not a write). */
    seenIntervalSeconds: 300,
};
const isKind = (v) => typeof v === 'string' && DEVICE_KINDS.includes(v);
const isRevokeReason = (v) => typeof v === 'string' && DEVICE_REVOKE_REASONS.includes(v);
/** A label: trimmed, inner whitespace collapsed, 1..80 characters, no control characters. */
export function normalizeDeviceLabel(raw) {
    if (typeof raw !== 'string')
        return err('DEVICE_LABEL_INVALID');
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(raw))
        return err('DEVICE_LABEL_INVALID');
    const label = raw.replace(/\s+/g, ' ').trim();
    if (label.length < 1 || label.length > DEVICE_PAIRING_POLICY.labelMaxLength)
        return err('DEVICE_LABEL_INVALID');
    return ok(label);
}
/** Register a device: the new, unpaired registration (scopes from the kind). */
export function registerDevice(input) {
    if (!isKind(input.kind))
        return err('DEVICE_KIND_INVALID');
    const label = normalizeDeviceLabel(input.label);
    if (!label.ok)
        return err(label.error);
    return ok({
        deviceId: input.deviceId,
        marketId: input.marketId,
        kind: input.kind,
        label: label.value,
        state: 'registered',
        scopes: DEVICE_SCOPES_BY_KIND[input.kind],
        registeredBy: input.by,
        registeredAt: input.at,
        policyVersion: DEVICE_PAIRING_POLICY_VERSION,
    });
}
/** A pairing code may be issued only for a registration that is still waiting to be paired. */
export function checkCodeIssue(device) {
    if (device.state === 'revoked')
        return err('DEVICE_REVOKED');
    if (device.state === 'paired')
        return err('DEVICE_ALREADY_PAIRED');
    return ok(true);
}
/** The code's expiry instant for a code issued at `at`. */
export function pairingCodeExpiry(at, ttlSeconds = DEVICE_PAIRING_POLICY.codeTtlSeconds) {
    return new Date(Date.parse(at) + ttlSeconds * 1000).toISOString();
}
/**
 * Whether `code` may pair `device` at `at`. The refusal is specific for the audit trail; the public surface never shows it
 * ({@link publicRedemptionRefusal}).
 */
export function checkRedemption(device, code, at) {
    if (device.state === 'revoked')
        return err('DEVICE_REVOKED');
    if (device.state === 'paired')
        return err('DEVICE_ALREADY_PAIRED');
    if (code.consumedAt !== undefined)
        return err('PAIRING_CODE_USED');
    if (code.supersededAt !== undefined)
        return err('PAIRING_CODE_SUPERSEDED');
    if (Date.parse(at) >= Date.parse(code.expiresAt))
        return err('PAIRING_CODE_EXPIRED');
    return ok(true);
}
/**
 * What the public redemption answers for ANY refusal: one reason, so the endpoint is no oracle for which codes exist, were used
 * or have expired. The rate limit keeps its own reason (the caller must wait).
 */
export function publicRedemptionRefusal(reason) {
    return reason === 'PAIRING_RATE_LIMITED' ? reason : 'PAIRING_CODE_INVALID';
}
/** Revoke: allowed once, for a registration that is not already revoked, with a reason from the closed list. */
export function checkRevoke(device, reason) {
    if (!isRevokeReason(reason))
        return err('DEVICE_REVOKE_REASON_INVALID');
    if (device.state === 'revoked')
        return err('DEVICE_REVOKED');
    return ok(reason);
}
/**
 * Delete ("Excluir"): a terminal soft delete, allowed only for a REVOKED registration (`DEVICE_NOT_REVOKED` otherwise - revoke first), once (a deleted registration is gone:
 * `DEVICE_NOT_FOUND`).
 */
export function checkDelete(device) {
    if (device.deletedAt !== undefined)
        return err('DEVICE_NOT_FOUND');
    if (device.state !== 'revoked')
        return err('DEVICE_NOT_REVOKED');
    return ok(true);
}
/** Deny-by-default: a principal may do what its kind's scopes list and nothing else. */
export function authorizeDeviceScope(principal, scope) {
    return principal.scopes.includes(scope) ? ok(true) : err('DEVICE_SCOPE_DENIED');
}
export function principalOf(device) {
    return { deviceId: device.deviceId, marketId: device.marketId, kind: device.kind, label: device.label, scopes: device.scopes };
}
//# sourceMappingURL=device.js.map